import { useMutation, useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import type { KeyboardEvent, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IMaskInput } from 'react-imask';
import {
  type VerificationCodeRequestParams,
  VerificationType,
} from '~/api/Customer';
import Icon from '~/components/Icon/Icon';
import Spinner from '~/components/Spinner/Spinner';
import { userStore } from '~/stores';
import copyToClipboard from '~/utils/copyToClipboard';

import InputText from '../InputText/InputText';
import DokobitErrorHandler from '../ModalAuthHeim/components/DokobitErrorHandler';
import styles from './ModalDokobitStatusCheck.module.scss';

export interface DokobitStatusCheckProps {
  step: 1 | 2 | 3;
  type: VerificationType;
  personalCode: string;
  phoneNumber?: string;
  dokobitCheckData: { token: string; code?: string } | null;
  dokobitError?: { title: Nullable<string>; body: Nullable<string> };
  captchaToken?: string;
  requestCodeWithDokobit: () => Promise<void>;
  onVerificationTypeSelect: (type: VerificationType) => void;
  onPersonalCodeChange: (personalCode: string) => void;
  onStepChange: (step: 1 | 2 | 3) => void;
  onError: (error: unknown) => void;
  /** Called when API returns method: 'sms' — switch to SMS code verification. */
  onSwitchToSmsVerification?: () => void;
  onSuccess?: () => void;
}

const ModalDokobitStatusCheck = ({
  step,
  type,
  personalCode,
  phoneNumber,
  dokobitCheckData,
  dokobitError,
  captchaToken,
  requestCodeWithDokobit,
  onVerificationTypeSelect,
  onPersonalCodeChange,
  onStepChange,
  onError,
  onSwitchToSmsVerification,
  onSuccess,
}: DokobitStatusCheckProps) => {
  const { t } = useTranslation();

  const copiedTimeoutRef =
    useRef<Nullable<ReturnType<typeof setTimeout>>>(null);
  const terminalErrorReportedRef = useRef(false);

  useEffect(() => {
    terminalErrorReportedRef.current = false;
  }, [dokobitCheckData?.token, dokobitCheckData?.code]);

  const [copied, setCopied] = useState(false);

  const isKennitalaValid = personalCode.replace(/\D/g, '').length === 10;

  const isStep3 = step === 3;

  const { mutate: sendDokobitCode, isPending: isSendingDokobitCode } =
    useMutation({
      mutationKey: ['sendDokobitCode', step, type, personalCode],
      mutationFn: requestCodeWithDokobit,
      onSuccess: () => onStepChange(3),
      onError,
    });

  const { isRefetching, isRefetchError, isStale } = useQuery({
    queryKey: ['checkVerificationStatus', dokobitCheckData, personalCode],
    queryFn: async ({ signal }) => {
      const { token, code } = dokobitCheckData ?? {};

      if (!token || !code || !phoneNumber || type === undefined) {
        return { result: false, error: null };
      }

      try {
        const verificationParams: VerificationCodeRequestParams =
          type === VerificationType.AUDKENNI
            ? { type, personal_code: personalCode ?? '' }
            : { type };

        const { verified, method } = await userStore.checkDokobitStatus({
          phone_number: `${phoneNumber}`.replace('+', ''),
          token: `${token}`,
          recaptcha_token: `${captchaToken}`,
          signal,
          ...verificationParams,
        });

        return {
          result: verified,
          method,
          error: null,
        };
      } catch (error) {
        return {
          result: false,
          method: undefined,
          error,
        };
      }
    },
    refetchInterval: (query) => {
      const data = query.state?.data;
      if (!data) {
        return false;
      }

      const { result, error, method } = data;

      const status = (error as AxiosError<{ status: string }>)?.response?.data
        ?.status;

      if (error && status !== 'waiting') {
        if (!terminalErrorReportedRef.current) {
          terminalErrorReportedRef.current = true;
          onError(error);
        }
        return false;
      }

      if (result) {
        if (method === 'sms') {
          onSwitchToSmsVerification?.();
        } else {
          onSuccess?.();
        }
        return false;
      }

      return !error || status === 'waiting' ? POLLING_TIMEOUT : false;
    },
    enabled:
      isStep3 &&
      !!dokobitCheckData?.token &&
      !!dokobitCheckData?.code &&
      !!phoneNumber &&
      type !== undefined,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
    gcTime: 0,
  });

  const isPolling = useMemo(() => {
    return isRefetching || isRefetchError || isStale;
  }, [isRefetching, isRefetchError, isStale]);

  const handleCopy = useCallback(() => {
    if (!dokobitCheckData?.code) {
      return;
    }

    if (copiedTimeoutRef.current) {
      clearTimeout(copiedTimeoutRef.current);
    }

    copyToClipboard({ text: dokobitCheckData.code });
    setCopied(true);

    copiedTimeoutRef.current = setTimeout(() => {
      setCopied(false);
      copiedTimeoutRef.current = null;
    }, 1500);
  }, [dokobitCheckData?.code]);

  const handleContinue = useCallback(() => {
    if (step === 1) {
      if (type === VerificationType.MOBILE) {
        return sendDokobitCode();
      }

      onStepChange(2);
      return;
    }

    if (step === 2 && type === VerificationType.AUDKENNI) {
      return sendDokobitCode();
    }

    onStepChange(3);
  }, [step, sendDokobitCode, type, personalCode]);

  useEffect(
    () => () => {
      if (copiedTimeoutRef.current) {
        clearTimeout(copiedTimeoutRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    const listener = confirmTabClosing(t('phrases:leaveVerificationBody'));
    const removeListener = () =>
      window.removeEventListener('beforeunload', listener);

    if (isStep3 && isPolling) {
      window.addEventListener('beforeunload', listener);
    } else {
      removeListener();
    }

    return removeListener;
  }, [isStep3, isPolling]);

  const handleVerificationOptionKeyDown = (
    e: KeyboardEvent<HTMLButtonElement>,
    provider: VerificationType,
  ) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onVerificationTypeSelect(provider);
    }
  };

  const getPartnerLabel = useCallback(
    (provider: VerificationType) =>
      t(
        provider === VerificationType.AUDKENNI
          ? 'phrases:verificationPartnerAudkenni'
          : 'phrases:verificationPartnerMobile',
      ),
    [t],
  );

  const partnerName = type !== undefined ? getPartnerLabel(type) : '';

  const StepLayout = ({
    heading,
    caption,
    children,
    footer,
    wrapInBody = false,
  }: {
    heading: ReactNode;
    caption?: ReactNode;
    children: ReactNode;
    footer?: ReactNode;
    wrapInBody?: boolean;
  }) => (
    <div className={styles.root}>
      {wrapInBody ? (
        <div className={styles.body}>
          <h2 className={styles.headingLabel}>{heading}</h2>
          {children}
          {caption != null && (
            <div>
              {typeof caption === 'string' ? (
                <p className={styles.captionLabel}>{caption}</p>
              ) : (
                caption
              )}
            </div>
          )}
          {isPolling && (
            <div className={styles.spinner}>
              <Spinner />
            </div>
          )}
        </div>
      ) : (
        <>
          <h2 className={styles.headingLabel}>{heading}</h2>
          {children}
          {caption != null && <p className={styles.captionLabel}>{caption}</p>}
          {isPolling && (
            <div className={styles.spinner}>
              <Spinner />
            </div>
          )}
          {footer}
        </>
      )}
    </div>
  );

  const continueButton = (
    <button
      type="button"
      className={classes(['button _primary _m', styles.continueButton])}
      onClick={handleContinue}
      disabled={isSendingDokobitCode}
    >
      {t('phrases:continue')}
    </button>
  );

  const stepViews = {
    1: (
      <StepLayout
        heading={t('phrases:ageVerification')}
        footer={continueButton}
      >
        <p className={styles.codeCaption}>
          {t('phrases:chooseVerificationMethod')}
        </p>
        <div className={styles.step1Options}>
          {[VerificationType.MOBILE, VerificationType.AUDKENNI].map(
            (provider) => (
              <button
                key={provider}
                type="button"
                className={classes([
                  styles.verificationOption,
                  {
                    [styles.verificationOptionActive!]: type === provider,
                  },
                ])}
                onClick={() => onVerificationTypeSelect(provider)}
                onKeyDown={(e) => handleVerificationOptionKeyDown(e, provider)}
                tabIndex={0}
                aria-label={getPartnerLabel(provider)}
              >
                <Icon type={`verif-${provider}`} size={24} />
                {getPartnerLabel(provider)}
              </button>
            ),
          )}
        </div>
      </StepLayout>
    ),
    2: (
      <StepLayout
        heading={t('phrases:ageVerificationVia', { partner: partnerName })}
        footer={
          <button
            type="button"
            className={classes(['button _primary _m', styles.continueButton])}
            onClick={handleContinue}
            disabled={!isKennitalaValid || isSendingDokobitCode}
          >
            {t('phrases:continue')}
          </button>
        }
      >
        <div className={styles.kennitalaInputWrapper}>
          <label>Kennitala</label>
          <InputText
            placeholder={t('phrases:enterNumber')}
            value={personalCode}
            onChange={(e) => onPersonalCodeChange(e.target.value)}
          >
            <IMaskInput
              data-input="kennitala-mask"
              className={classes(['input-text', styles.kennitalaInput])}
              mask="000000-0000"
              placeholder={t('phrases:enterNumber')}
              value={personalCode}
              unmask={true}
              type="text"
              autoFocus={true}
              onComplete={onPersonalCodeChange}
              autoComplete="kennitala"
            />
          </InputText>
          <DokobitErrorHandler dokobitError={dokobitError} />
        </div>
      </StepLayout>
    ),
    3: (
      <StepLayout
        wrapInBody
        heading={t('phrases:ageVerificationVia', { partner: partnerName })}
        caption={
          <>
            {t('phrases:useThisCodeForAgeVerification')
              .split('\n')
              .map((line) => (
                <p className={styles.captionLabel} key={line}>
                  {line}
                </p>
              ))}
          </>
        }
      >
        {!!dokobitCheckData?.code && (
          <>
            <p className={styles.codeCaption}>
              {t('phrases:yourSecurityCodeIs')}
            </p>
            <div className={styles.codeWrapper}>
              <div className={styles.code} role="button" onClick={handleCopy}>
                <span className={styles.codeLabel}>
                  {dokobitCheckData.code}
                </span>
                <Icon type={copied ? 'check' : 'copy'} />
              </div>
            </div>
          </>
        )}
      </StepLayout>
    ),
  };

  return stepViews[step];
};

export const POLLING_TIMEOUT = 2000;

const confirmTabClosing = (msg: string) => (e?: Event) => {
  e = e || window.event;

  if (e) {
    // @ts-ignore
    e.returnValue = msg;
  }

  return msg;
};

export default observer(ModalDokobitStatusCheck);
